import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import styled from '@emotion/styled';
import { useTranslation, Trans } from 'react-i18next';
import {
    HeadingText50,
    BodyTextMedium,
    ButtonGroup,
    Button,
    pxToRem,
} from '@modernhealth/vital-components';
import { font } from 'src/shared/styles/variables';

const PageContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 'calc(500px + 2 * 1rem)', // 500px + 2x padding
    padding: '0 1rem',
    margin: '3rem auto',
});

interface HeaderContainerProps {
    titleAlignment?: string;
    titleMarginTop: number;
}

const HeaderContainer = styled.div<HeaderContainerProps>`
    align-self: ${props => props.titleAlignment || 'center'};
    margin-top: ${props => pxToRem(props.titleMarginTop)};
`;

const DescriptionText = styled(BodyTextMedium)({
    maxWidth: 462,
    alignSelf: 'flex-start',
    textAlign: 'center',
});

const DescriptionBoldText = styled.span({
    ...font.heavy(font.size.base),
});

export interface PAMMCareOptionsSuccessProps {
    title: string;
    descriptionKey: string;
    firstButtonText: string;
    secondButtonText: string;
    onFirstButtonClick: () => void;
    onSecondButtonClick: () => void;
    Icon: React.FunctionComponent<
        React.SVGProps<SVGSVGElement> & {
            title?: string | undefined;
        }
    >;
}

export enum PAMMCareOptionsSuccessTestID {
    Container = 'PAMMCareOptionsSuccessTestID.Container',
    FirstButton = 'PAMMCareOptionsSuccessTestID.FirstButton',
    SecondButton = 'PAMMCareOptionsSuccessTestID.SecondButton',
}

export const PAMMCareOptionsSuccess: FC<PAMMCareOptionsSuccessProps> = ({
    title,
    descriptionKey,
    firstButtonText,
    secondButtonText,
    onFirstButtonClick,
    onSecondButtonClick,
    Icon,
}) => {
    const { t } = useTranslation();
    const user = useSelector(getUserInformation);

    return (
        <PageContainer data-testid={PAMMCareOptionsSuccessTestID.Container}>
            <Icon />
            <HeaderContainer titleMarginTop={0}>
                <HeadingText50>{title}</HeadingText50>
            </HeaderContainer>
            <DescriptionText fontColor="gray65" marginTop={10} marginBottom={16}>
                <Trans
                    t={t}
                    ns="providers"
                    i18nKey={descriptionKey}
                    values={{ email: user.email }}
                    components={{ highlight: <DescriptionBoldText /> }}
                />
            </DescriptionText>
            <ButtonGroup>
                <Button
                    size="large"
                    variant="secondary"
                    onClick={onFirstButtonClick}
                    data-testid={PAMMCareOptionsSuccessTestID.FirstButton}
                >
                    {firstButtonText}
                </Button>
                <Button
                    size="large"
                    variant="primary"
                    onClick={onSecondButtonClick}
                    data-testid={PAMMCareOptionsSuccessTestID.SecondButton}
                >
                    {secondButtonText}
                </Button>
            </ButtonGroup>
        </PageContainer>
    );
};
